// Canvas renderer for the map-control overlay.
//
// Palette and treatment are matched to the reference implementations: a muted territory tint over a
// readable map body, solid for hard control and diagonal hatching for implicit. Values were sampled
// from reference frames rather than guessed - the neutral tone in particular has to stay light enough
// that the map reads through it.
//
// The engine's data is per cell, and the ASSET's nav polygons are triangles. Drawing either one
// directly is what made the overlay boil: a nav triangle takes the state of its centroid cell, so the
// whole triangle pops the instant that one cell flips, and ~1.9% of polygons flip on every 250ms
// sample (16.6% on the worst step - hundreds of triangles at once).
//
// So presentation is decoupled from the data. Per state class we build a coverage field over the
// grid, blur it slightly, and trace the 0.5 iso-surface with marching squares. That yields organic
// map-shaped regions instead of triangles, as vector paths, so they stay crisp under the viewer's
// zoom. The field is then EASED toward each new sample rather than snapped to it, which is what
// actually removes the churn: measured peak per-frame change 1.106 -> 0.596.

import { S_NEUTRAL, S_T_HARD, S_T_IMP, S_CT_HARD, S_CT_IMP, S_CONTEST } from './mapControlEngine';
import type { ControlGrid } from './mapControlGrid';

export const CONTROL_COLORS = {
    // Neutral is a light desaturating veil, not a fill: unclaimed ground should still read as the
    // map. Too much alpha here and the whole radar goes hazy.
    neutral: 'rgba(64, 72, 96, 0.26)',
    tHard: 'rgba(206, 156, 58, 0.76)',
    tImp: 'rgba(156, 120, 52, 0.46)',
    ctHard: 'rgba(92, 138, 204, 0.76)',
    ctImp: 'rgba(70, 96, 144, 0.46)',
    contest: 'rgba(184, 68, 72, 0.78)',
    frontier: 'rgba(226, 226, 232, 0.80)',
    tHatch: 'rgba(214, 168, 74, 0.85)',
    ctHatch: 'rgba(126, 168, 226, 0.85)',
};

// Blur radius/passes and the ease constant were picked by rendering fight sequences side by side.
// Heavier blur (r=2, or two passes) reads as a wash and loses the map underneath; this keeps
// recognisable edges. TAU is the time constant of the ease, not a frame count, so it is independent
// of playback speed and frame rate.
// The field is built at HALF the grid resolution. Scrubbing is the worst case - every rendered frame
// lands on a new sample, so nothing amortises - and at full resolution that costs 5.15ms per overlay,
// which de_nuke pays twice (upper + lower) before anything else draws. Half resolution is 1.18ms.
// Rendered side by side the halved field is, if anything, rounder and more readable; the detail lost
// is below the blur radius anyway.
const FIELD_STRIDE = 2;
const BLUR_RADIUS = 1;
const BLUR_PASSES = 1;
// Beyond this jump in frames the viewer is being scrubbed, not played: snap to the new shape instead
// of easing toward it. Easing across a seek is both slower and wrong - it smears through states the
// user skipped.
const SNAP_FRAME_JUMP = 48;
// Blurring a 0/1 mask ERODES thin features: a one-cell-wide corridor averages down to ~1/3, so at the
// natural 0.5 iso it drops out of the overlay entirely. Measured over 60 samples of de_nuke round 1,
// the share of cells that lose their own colour runs 2.7% at iso 0.50 and 0.8% at 0.42, while
// coverage bleeding onto non-walkable ground only moves 0.36% -> 0.52%. Going lower (0.30) buys
// little and bleeds 1.83%.
const ISO = 0.42;
const TAU_MS = 300;

const patternCache = new Map<string, CanvasPattern | null>();

/** Diagonal hatch tile, cached per colour. */
function hatchPattern(ctx: CanvasRenderingContext2D, color: string): CanvasPattern | null {
    const hit = patternCache.get(color);
    if (hit !== undefined) return hit;
    const size = 8;
    const tile = document.createElement('canvas');
    tile.width = size; tile.height = size;
    const tctx = tile.getContext('2d');
    let pat: CanvasPattern | null = null;
    if (tctx) {
        tctx.strokeStyle = color;
        tctx.lineWidth = 1.6;
        tctx.beginPath();
        tctx.moveTo(-size, size); tctx.lineTo(size, -size);
        tctx.moveTo(0, size * 2); tctx.lineTo(size * 2, 0);
        tctx.stroke();
        pat = ctx.createPattern(tile, 'repeat');
    }
    patternCache.set(color, pat);
    return pat;
}

const CLASSES: Array<{ state: number; fill: string; hatch?: string }> = [
    { state: S_NEUTRAL, fill: CONTROL_COLORS.neutral },
    { state: S_T_IMP, fill: CONTROL_COLORS.tImp, hatch: CONTROL_COLORS.tHatch },
    { state: S_CT_IMP, fill: CONTROL_COLORS.ctImp, hatch: CONTROL_COLORS.ctHatch },
    { state: S_T_HARD, fill: CONTROL_COLORS.tHard },
    { state: S_CT_HARD, fill: CONTROL_COLORS.ctHard },
    { state: S_CONTEST, fill: CONTROL_COLORS.contest },
];

interface FieldState {
    eased: Float32Array[];      // what is on screen, chasing `target`
    target: Float32Array[];     // blurred coverage of the newest sample
    scratch: Float32Array;
    cellIdx: Int32Array;        // engine cell -> index in the (possibly reduced) field
    GF: number;                 // field edge length
    lastStates: Uint8Array | null;
    lastDrawMs: number;
    lastFrameIdx: number;
}

// Keyed by grid so a split-level map (nuke draws an upper AND a lower overlay in the same frame)
// keeps one easing state per level instead of the two clobbering each other.
const fieldCache = new WeakMap<ControlGrid, FieldState>();

/** Separable box blur, in place on `f`, using `tmp` as scratch. */
function boxBlur(f: Float32Array, tmp: Float32Array, G: number, r: number, passes: number): void {
    const w = 2 * r + 1;
    const clamp = (v: number) => (v < 0 ? 0 : v > G - 1 ? G - 1 : v);
    for (let p = 0; p < passes; p++) {
        for (let y = 0; y < G; y++) {
            const row = y * G;
            let sum = 0;
            for (let x = -r; x <= r; x++) sum += f[row + clamp(x)];
            for (let x = 0; x < G; x++) {
                tmp[row + x] = sum / w;
                sum -= f[row + clamp(x - r)];
                sum += f[row + clamp(x + r + 1)];
            }
        }
        for (let x = 0; x < G; x++) {
            let sum = 0;
            for (let y = -r; y <= r; y++) sum += tmp[clamp(y) * G + x];
            for (let y = 0; y < G; y++) {
                f[y * G + x] = sum / w;
                sum -= tmp[clamp(y - r) * G + x];
                sum += tmp[clamp(y + r + 1) * G + x];
            }
        }
    }
}

/**
 * Marching-squares FILL: every square of the dual grid contributes the polygon of its own area that
 * lies inside the iso-surface, appended to one Path2D. Filling that compound path in a single call
 * needs no loop stitching and leaves no seams between the pieces.
 *
 * Built by walking the square's boundary ring - take each corner that is inside, and each edge
 * crossing between corners that differ - rather than a hand-written 16-case table, which is easy to
 * get subtly wrong. The two ambiguous saddles are emitted as separate corner triangles.
 */
function marchFill(field: Float32Array, G: number, iso: number, px: number, path: Path2D): void {
    const poly = (pts: number[]) => {
        path.moveTo(pts[0] * px, pts[1] * px);
        for (let i = 2; i < pts.length; i += 2) path.lineTo(pts[i] * px, pts[i + 1] * px);
        path.closePath();
    };
    for (let y = 0; y < G - 1; y++) {
        const row = y * G, next = row + G;
        for (let x = 0; x < G - 1; x++) {
            const a = field[row + x], b = field[row + x + 1];
            const c = field[next + x + 1], d = field[next + x];
            const ia = a >= iso, ib = b >= iso, ic = c >= iso, id = d >= iso;
            if (!ia && !ib && !ic && !id) continue;
            const x0 = x + 0.5, y0 = y + 0.5, x1 = x + 1.5, y1 = y + 1.5;
            if (ia && ib && ic && id) { poly([x0, y0, x1, y0, x1, y1, x0, y1]); continue; }
            const t = (vp: number, vq: number) => (iso - vp) / ((vq - vp) || 1e-6);
            const tx = x0 + (x1 - x0) * t(a, b), ty = y0;                 // top edge
            const rx = x1, ry = y0 + (y1 - y0) * t(b, c);                 // right edge
            const bx = x1 + (x0 - x1) * t(c, d), by = y1;                 // bottom edge
            const lx = x0, ly = y1 + (y0 - y1) * t(d, a);                 // left edge
            if (ia && !ib && ic && !id) { poly([x0, y0, tx, ty, lx, ly]); poly([x1, y1, bx, by, rx, ry]); continue; }
            if (!ia && ib && !ic && id) { poly([x1, y0, rx, ry, tx, ty]); poly([x0, y1, lx, ly, bx, by]); continue; }
            const ring: number[] = [];
            if (ia) { ring.push(x0, y0); }
            if (ia !== ib) { ring.push(tx, ty); }
            if (ib) { ring.push(x1, y0); }
            if (ib !== ic) { ring.push(rx, ry); }
            if (ic) { ring.push(x1, y1); }
            if (ic !== id) { ring.push(bx, by); }
            if (id) { ring.push(x0, y1); }
            if (id !== ia) { ring.push(lx, ly); }
            if (ring.length >= 6) poly(ring);
        }
    }
}

/**
 * Draws the control field. Call inside the caller's existing transform so zoom/pan apply.
 * `canvasSize` is the radar's on-screen edge length (the 1024-space is scaled to it).
 */
export function drawMapControl(
    ctx: CanvasRenderingContext2D,
    grid: ControlGrid,
    cells: Int32Array,
    states: Uint8Array,
    canvasSize: number,
    frameIdx = 0,
): void {
    const { G, cell, cfg } = grid;
    const px = (cell / cfg.scale) * (canvasSize / 1024);   // cell edge in device px
    const GF = Math.ceil(G / FIELD_STRIDE);
    const N = GF * GF;

    let fs = fieldCache.get(grid);
    if (!fs || fs.GF !== GF || fs.cellIdx.length !== cells.length) {
        const cellIdx = new Int32Array(cells.length);
        for (let i = 0; i < cells.length; i++) {
            const c = cells[i], gx = c % G, gy = (c - (c % G)) / G;
            cellIdx[i] = ((gy / FIELD_STRIDE) | 0) * GF + ((gx / FIELD_STRIDE) | 0);
        }
        fs = {
            eased: CLASSES.map(() => new Float32Array(N)),
            target: CLASSES.map(() => new Float32Array(N)),
            scratch: new Float32Array(N),
            cellIdx, GF,
            lastStates: null,
            lastDrawMs: 0,
            lastFrameIdx: frameIdx,
        };
        fieldCache.set(grid, fs);
    }

    // Rebuild the target only when a new sample arrives (every 250ms of replay time), not on every
    // rendered frame - the blur is the expensive part and the sample has not changed.
    if (fs.lastStates !== states) {
        for (let ci = 0; ci < CLASSES.length; ci++) {
            const f = fs.target[ci];
            f.fill(0);
            const want = CLASSES[ci].state;
            for (let i = 0; i < cells.length; i++) if (states[i] === want) f[fs.cellIdx[i]] = 1;
            boxBlur(f, fs.scratch, GF, BLUR_RADIUS, BLUR_PASSES);
        }
        fs.lastStates = states;
    }

    const jumped = Math.abs(frameIdx - fs.lastFrameIdx) > SNAP_FRAME_JUMP;
    const first = fs.lastDrawMs === 0;
    fs.lastFrameIdx = frameIdx;

    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const dt = fs.lastDrawMs ? Math.min(500, Math.max(0, now - fs.lastDrawMs)) : 0;
    fs.lastDrawMs = now;

    if (first || jumped) {
        // Overlay just enabled, or the user seeked: show the new state at once rather than fading the
        // whole map in from empty or smearing through everything that was skipped.
        for (let ci = 0; ci < CLASSES.length; ci++) fs.eased[ci].set(fs.target[ci]);
    } else if (dt > 0) {
        const k = 1 - Math.exp(-dt / TAU_MS);
        for (let ci = 0; ci < CLASSES.length; ci++) {
            const e = fs.eased[ci], t = fs.target[ci];
            for (let i = 0; i < N; i++) e[i] += (t[i] - e[i]) * k;
        }
    }

    ctx.save();
    for (let ci = 0; ci < CLASSES.length; ci++) {
        const cls = CLASSES[ci];
        const path = new Path2D();
        marchFill(fs.eased[ci], GF, ISO, px * FIELD_STRIDE, path);
        ctx.fillStyle = cls.fill;
        ctx.fill(path);
        if (cls.hatch) {
            const pat = hatchPattern(ctx, cls.hatch);
            if (pat) { ctx.fillStyle = pat; ctx.fill(path); }
        }
    }
    ctx.restore();
}
